/**
 * DOCUMENTACIÓN (fase 4 de «la app sin ActiveCampaign», 07-10).
 *
 * Las reglas del ciclo, los planes y los correos, leídos del MISMO catálogo que usa el motor
 * y ya redactados por el servidor (GET /admin/documentacion, reglas/documentacion.py). Esta
 * pantalla solo lee: no hay nada que guardar. Lo que se filtra y se agrupa está en
 * lib/documentacion.js.
 *
 * LA ÚNICA COSA QUE ESCRIBE (10-10), y nunca en producción: «Probar» una regla sobre un
 * cliente de verdad (components/ProbarRegla.jsx). Si el servidor no la ofrece, el botón no sale.
 *
 * NINGUNA FRASE SE DECIDE AQUÍ. Qué está encendido en este entorno, la chapa de cada regla, el
 * recorrido de cada flecha y lo que el reloj hace además de las reglas vienen redactados del
 * servidor, que es quien sabe lo que hace el motor: la pantalla solo los pinta.
 *
 * Solo administradores, como el reloj y el simulador.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AsuntoYCuerpo from '../components/AsuntoYCuerpo';
import ProbarRegla, { BotonProbar, PruebasSinDevolver } from '../components/ProbarRegla';
import { toast } from 'sonner';
import { seEnlazaElSimulador } from '../lib/pantallasDelPanel';
import { ArrowRight, BookOpen, ChevronDown, ChevronUp, Copy, FlaskConical, Mail, Search } from 'lucide-react';
import {
    contarReglas, cuentaDeLaSemana, filasDelDiagrama, filtrarBloques, filtrarPlanes, listasDelDiagrama, reglasEnClaro,
    versionDelCatalogo,
} from '../lib/documentacion';

const PESTANAS = [['reglas', 'Reglas'], ['planes', 'Planes'], ['diagramas', 'Diagramas'],
    ['ademas', 'Además de las reglas'], ['pendientes', 'Pendientes']];

// El color de la chapa «qué hace el reloj con esta regla aquí». El texto lo manda el servidor.
const TONOS = {
    encendida: 'border-sky-500/30 text-sky-300 bg-sky-500/10',
    apagada: 'border-amber-500/30 text-amber-300 bg-amber-500/10',
    gris: 'border-[#333] text-white/70 bg-white/5',
};

const Chapa = ({ children, clase = 'border-[#333] text-white/70 bg-white/5', testid }) => (
    <span data-testid={testid} className={`inline-flex items-center gap-1.5 text-xs border rounded-full px-2.5 py-0.5 max-w-full ${clase}`}>
        <span className="min-w-0 break-words">{children}</span>
    </span>
);

const Buscador = ({ valor, onCambio, placeholder, testid }) => (
    <div className="relative w-full sm:max-w-[320px]">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input value={valor} onChange={(e) => onCambio(e.target.value)} placeholder={placeholder} data-testid={testid}
            className="w-full bg-[#0A0A0A] border border-[#2A2A2A] rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#FF671F]" />
    </div>
);

// Un dato de los detalles: la etiqueta arriba y el valor debajo (o una lista).
const Dato = ({ etiqueta, children }) => (
    <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-white/40 mb-1">{etiqueta}</p>
        <div className="text-sm text-white/80 break-words">{children}</div>
    </div>
);

const Lista = ({ items }) => (
    <ul className="list-disc pl-5 space-y-0.5">
        {items.map((t, i) => <li key={i}>{t}</li>)}
    </ul>
);

// El texto de un correo o de un aviso, tal cual está escrito, con sus variables explicadas.
const TextoQueRecibe = ({ icono, encabezado, asunto, cuerpo, variables, borrador, estado, notas }) => (
    <div className="rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] p-3 sm:p-4 space-y-2 min-w-0" data-testid="doc-texto">
        <div className="flex flex-wrap items-center gap-2">
            <span className="inline-flex items-center gap-1.5 text-sm text-white font-medium min-w-0 break-words">{icono}{encabezado}</span>
            {borrador
                ? <Chapa clase="border-amber-500/30 text-amber-300 bg-amber-500/10" testid="doc-borrador">Borrador</Chapa>
                : <Chapa clase="border-emerald-500/30 text-emerald-300 bg-emerald-500/10">Aprobado</Chapa>}
        </div>
        {borrador && <p className="text-xs text-amber-300/70">{estado}</p>}
        <AsuntoYCuerpo asunto={asunto} cuerpo={cuerpo} />
        {variables?.length > 0 && (
            <div className="text-xs text-white/50 space-y-0.5">
                {variables.map((v) => (
                    <p key={v.nombre} className="break-words"><span className="text-white/80 font-mono">{v.nombre}</span> {v.que_es}</p>
                ))}
            </div>
        )}
        {notas && <p className="text-xs text-white/40 break-words">{notas}</p>}
    </div>
);

const Regla = ({ r, relojManda, probar, onPrueba }) => {
    const [abierta, setAbierta] = useState(false);
    const [probando, setProbando] = useState(false);
    const cuenta = cuentaDeLaSemana(r);
    const d = r.detalles || {};
    return (
        <div className="rounded-lg border border-[#1E1E1E] bg-[#0E0E0E] p-3 sm:p-4 min-w-0" data-testid={`doc-regla-${r.numero}`}>
            <div className="flex flex-wrap items-center gap-2 mb-2">
                <span className="text-xs font-bold text-[#FF671F] border border-[#FF671F]/40 bg-[#FF671F]/10 rounded px-2 py-0.5">Regla {r.numero}</span>
                <h3 className="text-white font-semibold min-w-0 break-words">{r.titulo}</h3>
            </div>
            <p className="text-[15px] leading-relaxed text-white/90 break-words" data-testid={`doc-frase-${r.numero}`}>{r.frase}</p>
            {r.ademas?.length > 0 && (
                <div className="mt-3 border-l-2 border-[#FF671F]/50 pl-3 space-y-1.5" data-testid={`doc-ademas-${r.numero}`}>
                    <p className="text-[11px] uppercase tracking-wider text-white/40">Además, el reloj de la app</p>
                    {r.ademas.map((t) => <p key={t} className="text-sm text-white/70 break-words">{t}</p>)}
                </div>
            )}
            <div className="flex flex-wrap items-center gap-2 mt-3">
                {r.estado === 'pendiente'
                    ? <Chapa clase="border-amber-500/30 text-amber-300 bg-amber-500/10">Pendiente de confirmar</Chapa>
                    : <Chapa clase="border-emerald-500/30 text-emerald-300 bg-emerald-500/10">Confirmada</Chapa>}
                {r.reloj?.chapa?.texto && (
                    <Chapa testid={`doc-chapa-${r.numero}`} clase={TONOS[r.reloj.chapa.tono] || TONOS.gris}>{r.reloj.chapa.texto}</Chapa>
                )}
                {r.correos?.length > 0 && <Chapa><Mail className="w-3 h-3 inline mr-1" />Manda un correo</Chapa>}
                {relojManda && cuenta && (
                    <Chapa testid={`doc-veces-${r.numero}`}
                        clase={cuenta.seCuenta ? 'border-[#FF671F]/40 text-[#FF671F] bg-[#FF671F]/10' : TONOS.gris}>{cuenta.texto}</Chapa>
                )}
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mt-3">
                <button type="button" onClick={() => setAbierta(!abierta)} data-testid={`doc-detalles-${r.numero}`}
                    className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">
                    {abierta ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    {abierta ? 'Ocultar los detalles' : 'Ver los detalles'}
                </button>
                {/* Donde se puede (nunca en producción), «Probar» la ejecuta sobre un cliente de verdad;
                    el simulador, que no toca nada, sigue al lado. */}
                {probar?.se_puede && <BotonProbar regla={r} abierto={probando} onAlternar={() => setProbando(!probando)} />}
                {/* El enlace al simulador no se enseña de momento (13-10, `lib/pantallasDelPanel`). */}
                {seEnlazaElSimulador() && (
                    <Link to="/admin/simulador" data-testid={`doc-probar-${r.numero}`}
                        className={`inline-flex items-center gap-1 text-sm hover:underline ${probar?.se_puede ? 'text-white/60 hover:text-white' : 'text-[#FF671F]'}`}>
                        {!probar?.se_puede && <FlaskConical className="w-3.5 h-3.5" />}
                        {probar?.se_puede ? 'Simularla sin tocar nada' : 'Pruébala con un cliente'}
                    </Link>
                )}
            </div>
            {probando && probar?.se_puede && r.prueba?.se_puede && <ProbarRegla regla={r} donde={probar} onCambio={onPrueba} />}
            {abierta && (
                <div className="mt-4 pt-4 border-t border-[#1E1E1E] space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Dato etiqueta="Cuándo se inicia">{d.cuando}</Dato>
                        <Dato etiqueta="A quién afecta">{d.a_quien}</Dato>
                        <Dato etiqueta="Planes a los que llega hoy">
                            <div className="flex flex-wrap gap-1.5">{(d.llega_a || []).map((n) => <Chapa key={n}>{n}</Chapa>)}</div>
                        </Dato>
                        <Dato etiqueta="Condiciones">
                            {d.condiciones?.length > 0 ? <Lista items={d.condiciones} /> : 'Ninguna: se aplica siempre que ocurre.'}
                        </Dato>
                        <Dato etiqueta="Qué hace"><Lista items={d.que_hace || []} /></Dato>
                        <Dato etiqueta="Qué recibe el cliente">{d.recibe}</Dato>
                        {d.si_no && <Dato etiqueta="Si falla una condición">{d.si_no}</Dato>}
                        <Dato etiqueta="Estado">
                            {r.estado_en_claro}
                            {r.estado === 'pendiente' && r.por_defecto && <p className="text-amber-300/80 mt-1">Mientras tanto: {r.por_defecto}</p>}
                        </Dato>
                        <Dato etiqueta="Fuente">{r.fuente}</Dato>
                        {(r.reloj?.hora_decidida || r.reloj?.como || r.reloj?.sin_cuenta || r.reloj?.ciclos?.length > 0) && (
                            <Dato etiqueta="En el reloj de la app">
                                {r.reloj.ciclos?.length > 0 && <p>La ejecuta en: {r.reloj.ciclos.join('; ')}.</p>}
                                {r.reloj.como && <p>{r.reloj.como}.</p>}
                                {r.reloj.hora_decidida && <p>{r.reloj.hora_decidida}</p>}
                                {r.reloj.sin_cuenta?.porque && <p className="text-white/60">{r.reloj.sin_cuenta.porque}</p>}
                            </Dato>
                        )}
                    </div>
                    {r.tareas?.length > 0 && <Dato etiqueta="Tareas"><Lista items={r.tareas} /></Dato>}
                    {r.notas && <Dato etiqueta="Notas">{r.notas}</Dato>}
                    {r.ejemplos?.length > 0 && <Dato etiqueta="Ejemplos"><Lista items={r.ejemplos} /></Dato>}
                    {(r.correos || []).map((c) => (
                        <TextoQueRecibe key={c.nombre} icono={<Mail className="w-4 h-4 shrink-0 text-white/50" />}
                            encabezado={`Correo ${c.nombre}`} asunto={c.asunto} cuerpo={c.cuerpo}
                            variables={c.variables} borrador={c.borrador} estado={c.estado} notas={c.notas} />
                    ))}
                    {(r.avisos || []).map((a) => (
                        <TextoQueRecibe key={a.titulo} icono={<BookOpen className="w-4 h-4 shrink-0 text-white/50" />}
                            encabezado="Aviso que le sale en la app" asunto={a.titulo} cuerpo={a.cuerpo}
                            variables={a.variables} borrador={a.borrador} estado={a.estado} notas={a.notas} />
                    ))}
                </div>
            )}
        </div>
    );
};

const Bloque = ({ bloque, relojManda, forzarAbierto, probar, onPrueba }) => {
    const [abierto, setAbierto] = useState(true);
    const seVe = abierto || forzarAbierto;
    return (
        <Card className="bg-[#111] border-[#222]" data-testid="doc-bloque">
            <CardContent className="p-4 sm:p-5">
                <button type="button" onClick={() => setAbierto(!abierto)}
                    className="w-full flex items-center justify-between gap-3 text-left">
                    <span className="min-w-0">
                        <span className="block text-xl font-bold text-white uppercase break-words" style={{ fontFamily: 'Barlow Condensed' }}>{bloque.titulo}</span>
                        <span className="block text-xs text-white/45">{bloque.reglas.length} {bloque.reglas.length === 1 ? 'regla' : 'reglas'}</span>
                    </span>
                    {seVe ? <ChevronUp className="w-5 h-5 text-white/50 shrink-0" /> : <ChevronDown className="w-5 h-5 text-white/50 shrink-0" />}
                </button>
                {seVe && (
                    <div className="space-y-3 mt-4">
                        {bloque.reglas.map((r) => <Regla key={r.id} r={r} relojManda={relojManda} probar={probar} onPrueba={onPrueba} />)}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const PestanaReglas = ({ doc }) => {
    const { api } = useAuth();
    const [buscar, setBuscar] = useState('');
    // Los clientes con una prueba sin devolver. Solo se pregunta donde se puede probar.
    const probar = doc.cabecera?.probar;
    const [sinDevolver, setSinDevolver] = useState([]);
    const mirarPruebas = useCallback(() => {
        if (!probar?.se_puede) return;
        api.get('/admin/documentacion/probar')
            .then((res) => setSinDevolver(res.data?.sin_devolver || []))
            .catch((e) => console.error('[documentacion] pruebas sin devolver', e));
    }, [api, probar?.se_puede]);
    useEffect(() => { mirarPruebas(); }, [mirarPruebas]);
    const bloques = useMemo(() => filtrarBloques(doc.bloques, buscar), [doc, buscar]);
    const total = contarReglas(doc.bloques);
    const quedan = contarReglas(bloques);
    const relojManda = !!doc.cabecera?.reloj?.manda;
    return (
        <div className="space-y-4" data-testid="doc-reglas">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Buscador valor={buscar} onCambio={setBuscar} placeholder="Número de regla o texto…" testid="doc-buscar" />
                <p className="text-xs text-white/45" data-testid="doc-cuenta">
                    {buscar.trim() ? `${quedan} de ${total} reglas` : `${total} reglas, en ${doc.bloques.length} bloques`}
                </p>
            </div>
            <PruebasSinDevolver pendientes={sinDevolver} onDevuelto={mirarPruebas} />
            {!buscar.trim() && (doc.politicas || []).map((p) => (
                <Card key={p.id} className="bg-[#111] border-[#FF671F]/30" data-testid="doc-politica">
                    <CardContent className="p-4 sm:p-5 space-y-2">
                        <div className="flex flex-wrap items-center gap-2">
                            <Chapa clase="border-[#FF671F]/40 text-[#FF671F] bg-[#FF671F]/10">Vale por encima de todas las reglas</Chapa>
                            <h3 className="text-white font-semibold min-w-0 break-words">{p.titulo}</h3>
                        </div>
                        <p className="text-[15px] leading-relaxed text-white/90 break-words">{p.frase}</p>
                        {p.notas && <p className="text-sm text-white/50 break-words">{p.notas}</p>}
                    </CardContent>
                </Card>
            ))}
            {bloques.length === 0
                ? <p className="text-white/50 text-sm" data-testid="doc-sin-resultados">Ninguna regla coincide con lo que buscas.</p>
                : bloques.map((b) => (
                    <Bloque key={b.titulo} bloque={b} relojManda={relojManda} forzarAbierto={!!buscar.trim()}
                        probar={probar} onPrueba={mirarPruebas} />
                ))}
        </div>
    );
};

const ReglasDelPlan = ({ reglas }) => (reglas?.length > 0 ? (
    <ul className="space-y-1">
        {reglas.map((r) => (
            <li key={r.id} className="text-sm text-white/70 break-words">
                <span className="text-[#FF671F] font-semibold">Regla {r.numero}</span> {r.titulo}
                {r.nota && <span className="block text-xs text-white/45">{r.nota}</span>}
            </li>
        ))}
    </ul>
) : <p className="text-sm text-white/45">Ninguna regla del ciclo le afecta.</p>);

const PestanaPlanes = ({ doc }) => {
    const [buscar, setBuscar] = useState('');
    const [abierto, setAbierto] = useState(null);
    const planes = useMemo(() => filtrarPlanes(doc.planes, buscar), [doc, buscar]);
    const alternar = (nombre) => setAbierto(abierto === nombre ? null : nombre);
    return (
        <div className="space-y-4" data-testid="doc-planes">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <Buscador valor={buscar} onCambio={setBuscar} placeholder="Buscar un plan…" testid="doc-buscar-plan" />
                <p className="text-xs text-white/45">{planes.length} de {doc.planes.length} planes</p>
            </div>
            {planes.length === 0 && <p className="text-white/50 text-sm">Ningún plan coincide con lo que buscas.</p>}

            {/* En móvil, una tarjeta por plan: una tabla de siete columnas no cabe en 390 px. */}
            <div className="md:hidden space-y-3" data-testid="doc-planes-movil">
                {planes.map((p) => (
                    <div key={p.nombre} className="rounded-lg border border-[#222] bg-[#111] p-4 space-y-3 min-w-0">
                        <button type="button" onClick={() => alternar(p.nombre)} className="w-full flex items-start justify-between gap-2 text-left">
                            <span className="min-w-0">
                                <span className="block text-white font-semibold break-words">{p.nombre}</span>
                                <span className="block text-xs text-white/50 break-words">{p.estado}</span>
                            </span>
                            {abierto === p.nombre ? <ChevronUp className="w-4 h-4 text-white/50 shrink-0 mt-1" /> : <ChevronDown className="w-4 h-4 text-white/50 shrink-0 mt-1" />}
                        </button>
                        <div className="grid grid-cols-1 gap-3">
                            <Dato etiqueta="Ciclo">{p.ciclo}</Dato>
                            <Dato etiqueta="Reportes">{p.reportes?.length > 0 ? p.reportes.join('; ') : 'Ninguno'}</Dato>
                            <Dato etiqueta="Rutina">{p.rutina}</Dato>
                            <Dato etiqueta="Ajuste de macros">{p.ajuste}</Dato>
                            <Dato etiqueta="Semanas">{p.semanas}</Dato>
                        </div>
                        {abierto === p.nombre && (
                            <div className="pt-3 border-t border-[#1E1E1E] space-y-3">
                                <Dato etiqueta="Renovación">{p.renovacion}</Dato>
                                <Dato etiqueta={`Reglas que le afectan (${p.reglas.length})`}><ReglasDelPlan reglas={p.reglas} /></Dato>
                                {p.notas && <Dato etiqueta="Notas">{p.notas}</Dato>}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Desde tablet, la tabla. Se desplaza dentro de su caja, nunca la página. */}
            {planes.length > 0 && (
                <Card className="hidden md:block bg-[#111] border-[#222]">
                    <CardContent className="p-0">
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm min-w-[920px]" data-testid="doc-planes-tabla">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-wider text-white/40 border-b border-[#222]">
                                        {['Plan', 'Estado', 'Ciclo', 'Reportes', 'Rutina', 'Ajuste de macros', 'Semanas', 'Reglas'].map((t) => (
                                            <th key={t} className="px-4 py-3 font-medium">{t}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {planes.map((p) => (
                                        <React.Fragment key={p.nombre}>
                                            <tr onClick={() => alternar(p.nombre)} data-testid="doc-plan"
                                                className="border-b border-[#1A1A1A] align-top cursor-pointer hover:bg-white/[0.03]">
                                                <td className="px-4 py-3 text-white font-semibold">{p.nombre}</td>
                                                <td className="px-4 py-3 text-white/70">{p.estado}</td>
                                                <td className="px-4 py-3 text-white/70">{p.ciclo}</td>
                                                <td className="px-4 py-3 text-white/70">{p.reportes?.length > 0 ? p.reportes.join('; ') : 'Ninguno'}</td>
                                                <td className="px-4 py-3 text-white/70">{p.rutina}</td>
                                                <td className="px-4 py-3 text-white/70">{p.ajuste}</td>
                                                <td className="px-4 py-3 text-white/70">{p.semanas}</td>
                                                <td className="px-4 py-3 text-white/70 whitespace-nowrap">
                                                    {p.reglas.length} {abierto === p.nombre ? <ChevronUp className="w-4 h-4 inline" /> : <ChevronDown className="w-4 h-4 inline" />}
                                                </td>
                                            </tr>
                                            {abierto === p.nombre && (
                                                <tr className="border-b border-[#1A1A1A] bg-[#0C0C0C]">
                                                    <td colSpan={8} className="px-4 py-4">
                                                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 max-w-5xl">
                                                            <Dato etiqueta={`Reglas que le afectan (${p.reglas.length})`}><ReglasDelPlan reglas={p.reglas} /></Dato>
                                                            <div className="space-y-4">
                                                                <Dato etiqueta="Renovación">{p.renovacion}</Dato>
                                                                {p.notas && <Dato etiqueta="Notas">{p.notas}</Dato>}
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}
        </div>
    );
};

// El número de una regla en el dibujo. Atenuada si el reloj de la app no la ejecuta.
const Numero = ({ r }) => (
    <span title={`Regla ${r.numero}: ${r.titulo}`}
        className={`inline-block text-xs font-bold rounded px-1.5 py-0.5 border ${r.la_mueve_el_reloj
            ? 'text-[#FF671F] border-[#FF671F]/40 bg-[#FF671F]/10' : 'text-white/50 border-[#333] bg-white/5'}`}>R{r.numero}{r.solo_algunos_planes ? '*' : ''}</span>
);

// Una regla debajo del dibujo: por dónde pasa, cuándo y con qué condiciones. Todo viene
// redactado del servidor, con la semana real de cada largo de ciclo.
const ReglaDelDibujo = ({ r }) => (
    <li className="text-sm text-white/70 break-words" data-testid={`doc-dibujo-regla-${r.numero}`}>
        <Numero r={r} /> <span className="text-white">{r.recorrido || r.titulo}</span>
        <span className="block text-xs text-white/45 mt-0.5">
            {r.recorrido ? `${r.titulo}. ` : ''}Se inicia {r.cuando}{r.si && r.si !== 'siempre' ? `, si ${r.si}` : ''}.
        </span>
        {r.alcance && <span className="block text-xs text-amber-300/80 mt-0.5">{r.alcance}</span>}
        {r.nota && <span className="block text-xs text-white/60 mt-0.5">{r.nota}</span>}
    </li>
);

// Una fila de semanas: las de un largo de ciclo, con lo que pasa dentro de cada una y sobre
// cada flecha.
const FilaDeSemanas = ({ fila }) => (
    <div data-testid="doc-diagrama-semanas">
        {fila.titulo && <p className="text-[11px] uppercase tracking-wider text-white/40 mb-2">{fila.titulo}</p>}
        <div className="overflow-x-auto pb-2">
            <div className="flex items-stretch gap-2 min-w-max">
                {fila.semanas.map((s, i) => (
                    <React.Fragment key={s.nombre}>
                        <div className="w-[150px] rounded-lg border border-[#2A2A2A] bg-[#0A0A0A] p-3 flex flex-col gap-2">
                            <p className="text-white font-bold uppercase" style={{ fontFamily: 'Barlow Condensed' }}>{s.nombre}</p>
                            {s.dentro.length > 0
                                ? <div className="flex flex-wrap gap-1">{s.dentro.map((r) => <Numero key={r.id} r={r} />)}</div>
                                : <p className="text-[11px] text-white/30">Nada propio</p>}
                        </div>
                        {i < fila.semanas.length - 1 && (
                            <div className="flex flex-col items-center justify-center gap-1 w-[52px]">
                                <div className="flex flex-wrap justify-center gap-1">{s.siguiente.map((r) => <Numero key={r.id} r={r} />)}</div>
                                <ArrowRight className={`w-5 h-5 ${s.siguiente.length > 0 ? 'text-[#FF671F]' : 'text-white/15'}`} />
                            </div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    </div>
);

const copiar = async (texto) => {
    try {
        await navigator.clipboard.writeText(texto);
        toast.success('Diagrama copiado');
    } catch (e) {
        console.error('[documentacion] no se pudo copiar', e);
        toast.error('No se pudo copiar. Selecciona el texto y cópialo a mano.');
    }
};

const Diagrama = ({ diagrama }) => {
    const [verTexto, setVerTexto] = useState(false);
    const filas = useMemo(() => filasDelDiagrama(diagrama), [diagrama]);
    const { cambian, noCambian } = useMemo(() => listasDelDiagrama(diagrama), [diagrama]);
    return (
        <Card className="bg-[#111] border-[#222]" data-testid="doc-diagrama">
            <CardContent className="p-4 sm:p-5 space-y-4">
                <div>
                    <h3 className="text-xl font-bold text-white uppercase break-words" style={{ fontFamily: 'Barlow Condensed' }}>{diagrama.tipo}</h3>
                    <p className="text-sm text-white/55 break-words">{diagrama.descripcion}.</p>
                    <div className="flex flex-wrap gap-1.5 mt-2">{(diagrama.planes || []).map((n) => <Chapa key={n}>{n}</Chapa>)}</div>
                </div>

                {filas.map((fila, i) => <FilaDeSemanas key={fila.titulo || i} fila={fila} />)}

                {cambian.length > 0 && (
                    <Dato etiqueta="Cambios de semana">
                        <ul className="space-y-2">{cambian.map((r) => <ReglaDelDibujo key={r.id} r={r} />)}</ul>
                    </Dato>
                )}
                {noCambian.length > 0 && (
                    <Dato etiqueta={filas.length > 0 ? 'Reglas que actúan sin cambiar la semana' : 'Sus reglas (este ciclo no cuenta semanas)'}>
                        <ul className="space-y-2">{noCambian.map((r) => <ReglaDelDibujo key={r.id} r={r} />)}</ul>
                    </Dato>
                )}

                {diagrama.mermaid && (
                    <div className="pt-3 border-t border-[#1E1E1E]">
                        <div className="flex flex-wrap items-center gap-3">
                            <button type="button" onClick={() => setVerTexto(!verTexto)} className="inline-flex items-center gap-1 text-sm text-white/60 hover:text-white">
                                {verTexto ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                {verTexto ? 'Ocultar el diagrama en texto' : 'Ver el diagrama en texto (Mermaid)'}
                            </button>
                            <Button onClick={() => copiar(diagrama.mermaid)} data-testid="doc-copiar-mermaid"
                                className="h-8 bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white text-xs px-3">
                                <Copy className="w-3.5 h-3.5 mr-1.5" />Copiar
                            </Button>
                        </div>
                        {verTexto && (
                            <pre className="mt-3 text-xs text-white/70 bg-[#0A0A0A] border border-[#1E1E1E] rounded-lg p-3 overflow-x-auto max-h-[360px]">{diagrama.mermaid}</pre>
                        )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const PestanaDiagramas = ({ doc }) => (
    <div className="space-y-4" data-testid="doc-diagramas">
        {(doc.diagramas || []).map((d) => <Diagrama key={d.tipo} diagrama={d} />)}
    </div>
);

const PestanaAdemas = ({ doc }) => {
    const ademas = doc.ademas || {};
    return (
        <div className="space-y-4" data-testid="doc-ademas">
            <div className="max-w-3xl">
                <h2 className="text-2xl font-bold text-white uppercase break-words" style={{ fontFamily: 'Barlow Condensed' }}>{ademas.titulo}</h2>
                <p className="text-sm text-white/50 mt-1 break-words">{ademas.entrada}</p>
            </div>
            {(ademas.grupos || []).map((g) => (
                <Card key={g.titulo} className="bg-[#111] border-[#222]" data-testid="doc-ademas-grupo">
                    <CardContent className="p-4 sm:p-5 space-y-3">
                        <h3 className="text-xl font-bold text-white uppercase break-words" style={{ fontFamily: 'Barlow Condensed' }}>{g.titulo}</h3>
                        <ul className="space-y-3">
                            {(g.puntos || []).map((p) => (
                                <li key={p.texto} className="text-sm text-white/80 break-words">
                                    {p.texto}
                                    {p.reglas?.length > 0 && (
                                        <span className="block text-xs text-white/45 mt-0.5">Afecta a {reglasEnClaro(p.reglas)}.</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </CardContent>
                </Card>
            ))}
        </div>
    );
};

const PestanaPendientes = ({ doc }) => {
    const p = doc.pendientes || {};
    const reglas = p.reglas || [];
    const planes = p.planes || [];
    const borradores = p.textos_en_borrador || [];
    return (
        <div className="space-y-4" data-testid="doc-pendientes">
            <Card className="bg-[#111] border-[#222]">
                <CardContent className="p-4 sm:p-5 space-y-3">
                    <h3 className="text-xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed' }}>Reglas sin confirmar</h3>
                    {reglas.length === 0 ? (
                        <p className="text-sm text-emerald-300/90" data-testid="doc-sin-pendientes">No hay ninguna regla pendiente de confirmar.</p>
                    ) : (
                        <ul className="space-y-3">
                            {reglas.map((r) => (
                                <li key={r.id} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm break-words" data-testid="doc-pendiente">
                                    <p className="text-white font-semibold">Regla {r.numero}. {r.titulo}</p>
                                    <p className="text-white/70 mt-1">Mientras nadie la confirme, el motor aplica: <span className="text-white">{r.por_defecto}</span></p>
                                    {r.punto_abierto && <p className="text-white/50 mt-1">De dónde sale la duda: {r.punto_abierto}</p>}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <Card className="bg-[#111] border-[#222]">
                <CardContent className="p-4 sm:p-5 space-y-3">
                    <h3 className="text-xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed' }}>Planes sin confirmar</h3>
                    {planes.length === 0 ? (
                        <p className="text-sm text-emerald-300/90">No hay ningún plan pendiente de confirmar.</p>
                    ) : (
                        <ul className="space-y-3">
                            {planes.map((x) => (
                                <li key={x.nombre} className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3 text-sm break-words">
                                    <p className="text-white font-semibold">{x.nombre}</p>
                                    <p className="text-white/70 mt-1">Mientras nadie lo confirme, el motor aplica: <span className="text-white">{x.por_defecto}</span></p>
                                    {x.punto_abierto && <p className="text-white/50 mt-1">De dónde sale la duda: {x.punto_abierto}</p>}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>

            <Card className="bg-[#111] border-[#222]">
                <CardContent className="p-4 sm:p-5 space-y-3">
                    <h3 className="text-xl font-bold text-white uppercase" style={{ fontFamily: 'Barlow Condensed' }}>Textos en borrador</h3>
                    {borradores.length === 0 ? (
                        <p className="text-sm text-emerald-300/90">Todos los textos están aprobados.</p>
                    ) : (
                        <ul className="space-y-2" data-testid="doc-borradores">
                            {borradores.map((b) => (
                                <li key={`${b.tipo}-${b.nombre}`} className="rounded-lg border border-[#1E1E1E] bg-[#0E0E0E] p-3 text-sm break-words">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Chapa clase="border-amber-500/30 text-amber-300 bg-amber-500/10">Borrador</Chapa>
                                        <span className="text-white font-medium">{b.tipo} {b.nombre}</span>
                                    </div>
                                    <p className="text-white/60 mt-1">{b.tipo === 'Correo' ? 'Asunto' : 'Título'}: {b.asunto}</p>
                                    {b.reglas?.length > 0 && (
                                        <p className="text-white/45 mt-1">Lo usa {b.reglas.length === 1 ? 'la regla' : 'las reglas'} {b.reglas.map((id) => id.replace(/^R/, '')).join(', ')}.</p>
                                    )}
                                </li>
                            ))}
                        </ul>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

const AdminDocumentacionPage = () => {
    const { api } = useAuth();
    const [doc, setDoc] = useState(null);
    const [fallo, setFallo] = useState(false);
    const [pestana, setPestana] = useState('reglas');

    const cargar = () => {
        setFallo(false);
        return api.get('/admin/documentacion')
            .then((r) => setDoc(r.data))
            .catch((e) => { console.error('[documentacion]', e); setFallo(true); });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo al entrar
    useEffect(() => { cargar(); }, []);

    if (fallo) {
        return (
            <div className="p-4 sm:p-6 space-y-3" data-testid="doc-error">
                <p className="text-white/70">No se pudo cargar la documentación. Inténtalo de nuevo en un momento.</p>
                <Button onClick={cargar} className="bg-[#1F1F1F] hover:bg-[#2A2A2A] text-white">Volver a intentarlo</Button>
            </div>
        );
    }
    if (!doc) return <div className="p-6 text-white/50">Cargando la documentación…</div>;

    const cabecera = doc.cabecera || {};
    const reloj = cabecera.reloj || {};
    const entorno = cabecera.entorno || {};
    const pendientes = (doc.pendientes?.reglas?.length || 0) + (doc.pendientes?.planes?.length || 0);
    const fuera = reloj.fuera_de_las_maquinas || [];

    return (
        <div className="p-4 sm:p-6 space-y-5 max-w-[1400px] min-w-0" data-testid="documentacion">
            <div className="flex flex-wrap items-start justify-between gap-3">
                <h1 className="text-3xl sm:text-4xl font-bold text-white uppercase min-w-0" style={{ fontFamily: 'Barlow Condensed' }}>
                    Documentación
                </h1>
                {/* Donde se puede probar una regla sobre un cliente, la página ya no es solo de lectura. */}
                {cabecera.probar?.se_puede ? (
                    <span className="inline-flex items-center gap-1.5 text-xs text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-full px-3 py-1" data-testid="doc-con-pruebas">
                        <FlaskConical className="w-3.5 h-3.5" /> Aquí se pueden probar las reglas
                    </span>
                ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs text-emerald-400 border border-emerald-500/30 bg-emerald-500/10 rounded-full px-3 py-1">
                        <BookOpen className="w-3.5 h-3.5" /> Solo lectura
                    </span>
                )}
            </div>

            <div className="flex flex-wrap gap-2" data-testid="doc-version">
                {versionDelCatalogo(cabecera).map((t) => <Chapa key={t} clase="border-[#333] text-white bg-[#1A1A1A]">{t}</Chapa>)}
                {cabecera.desplegado?.commit && (
                    <Chapa>Versión desplegada: {cabecera.desplegado.commit}{cabecera.desplegado.rama ? ` (${cabecera.desplegado.rama})` : ''}</Chapa>
                )}
            </div>

            {/* Qué está encendido en ESTE entorno. Lo redacta el servidor: en producción el reloj
                va en sombra y la página no puede decir que lo que se lee es lo que pasa. */}
            {entorno.titular && (
                <div data-testid="doc-entorno"
                    className={`rounded-lg border p-4 space-y-3 max-w-4xl ${entorno.gobierna
                        ? 'border-emerald-500/30 bg-emerald-500/5' : 'border-amber-500/40 bg-amber-500/5'}`}>
                    <p className={`text-[15px] font-semibold break-words ${entorno.gobierna ? 'text-emerald-300' : 'text-amber-300'}`}
                        data-testid="doc-entorno-titular">{entorno.titular}</p>
                    <ul className="space-y-2">
                        {(entorno.puntos || []).map((p) => (
                            <li key={p.que} className="text-sm break-words" data-testid="doc-entorno-punto">
                                <span className="flex flex-wrap items-center gap-2">
                                    <span className="text-white font-medium">{p.que}</span>
                                    <Chapa clase={p.encendido ? TONOS.encendida : TONOS.apagada}>{p.encendido ? 'Encendido' : 'Apagado'}</Chapa>
                                </span>
                                <span className="block text-white/60 mt-0.5">{p.texto}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="text-sm text-white/55 space-y-1 max-w-3xl" data-testid="doc-reloj">
                {reloj.manda && (
                    <p>
                        En cada regla verás cuántas veces se ha aplicado esta semana.
                        {!reloj.recuento ? ' Ahora mismo no se pudo hacer esa cuenta.' : ''}
                    </p>
                )}
                <p>
                    El reloj ejecuta {(reloj.reglas_que_mueve || []).length} de las {cabecera.numero_de_reglas} reglas tal como están
                    escritas.{fuera.length > 0 ? ' Las demás van de otra manera:' : ''}
                </p>
                {fuera.length > 0 && (
                    <ul className="list-disc pl-5 space-y-0.5" data-testid="doc-fuera-de-las-maquinas">
                        {fuera.map((x) => <li key={x.id} className="break-words"><span className="text-white/80">Regla {x.numero}.</span> {x.texto}</li>)}
                    </ul>
                )}
            </div>

            <div className="flex flex-wrap gap-1.5" data-testid="doc-pestanas">
                {PESTANAS.map(([k, t]) => (
                    <button key={k} type="button" onClick={() => setPestana(k)} data-testid={`doc-pestana-${k}`}
                        className={`text-sm px-3 py-1.5 rounded-lg ${pestana === k ? 'bg-[#FF671F] text-white' : 'bg-[#1A1A1A] text-white/60 hover:text-white'}`}>
                        {t}{k === 'pendientes' ? ` (${pendientes})` : ''}
                    </button>
                ))}
            </div>

            {pestana === 'reglas' && <PestanaReglas doc={doc} />}
            {pestana === 'planes' && <PestanaPlanes doc={doc} />}
            {pestana === 'diagramas' && <PestanaDiagramas doc={doc} />}
            {pestana === 'ademas' && <PestanaAdemas doc={doc} />}
            {pestana === 'pendientes' && <PestanaPendientes doc={doc} />}
        </div>
    );
};

export default AdminDocumentacionPage;
